import type { MouseEvent, ComponentType } from 'react';
import { useCallback, useContext } from 'react';
import classnames from 'classnames';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import { BiEvent } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { GallectionType } from '../../../../../../types/gallection';
import { ShopItemType } from '../../../../../../types/shop';
import EmptyState from '../../../../EmptyState';
import EmptyStateSubtitleWithLearnMoreLink from '../EmptyStateSubtitleWithLearnMoreLink';
import camelCase from 'lodash/camelCase';

import s from './EmptyState.scss';

const enhance = compose(withRouter) as <P>(
  Component: ComponentType<P>
) => ComponentType<P>;

export interface Props {
  title?: string;
  subtitle?: string;
  view?: 'large' | 'wide';
  shopItemType?: ShopItemType;
  isOwner: boolean;
  user: PapiUser;
  buttonLabel?: string;
  onButtonClick?: (ev?: MouseEvent) => void;
  biData?: BiEvent;
  onOpenChat: (user: PapiUser) => void;
}

export function ShopEmptyState(props: Props & RouteComponentProps<'history'>) {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const {
    shopItemType = 'all',
    view,
    title,
    subtitle,
    isOwner,
    user: { username },
    buttonLabel,
    onButtonClick,
    biData,
    history,
  } = props;

  const navigateToGallery = useCallback(() => {
    const galleryPath = Url.userGalleryPath({
      username,
      gallectionType: GallectionType.GALLERY,
    });
    history.push(galleryPath);
  }, [history, username]);

  const handleOpenChat = useCallback(() => {
    window.open(Url.userMessageLink(props.user.username), '_blank');
  }, [props.user]);

  const shopItemTypeTranslationKey = camelCase(shopItemType);
  const translationMode = isOwner
    ? isMobile
      ? 'owner.mobile'
      : 'owner'
    : 'visitor';

  return (
    <EmptyState
      className={classnames(s['root'], view === 'large' && s['large'])}
      titleClassName={s['title']}
      subtitleClassName={s['subtitle']}
      view={view}
      title={
        title ||
        t(
          `pages.shop.emptyState.${shopItemTypeTranslationKey}.title.${translationMode}`,
          {
            username,
          }
        )
      }
      subtitle={
        <EmptyStateSubtitleWithLearnMoreLink
          shopItemType={shopItemType}
          subtitle={
            subtitle ||
            t(
              `pages.shop.emptyState.${shopItemTypeTranslationKey}.subtitle.${translationMode}`
            )
          }
        />
      }
      buttonLabel={
        buttonLabel ||
        (isOwner
          ? t('pages.shop.emptyState.goToGallery')
          : t('pages.shop.emptyState.openChatWithArtist'))
      }
      onButtonClick={
        onButtonClick || (isOwner ? navigateToGallery : handleOpenChat)
      }
      withButton
      biData={biData}
    />
  );
}

export default enhance(ShopEmptyState);
